import React, { useState } from 'react';
import { useSettings } from './contexts/SettingsContext';
import { useAPIKeys } from './contexts/APIKeyContext';
import APIKeySetup from './components/APIKeySetup';

const Settings = () => {
  const {
    settings,
    updateGradingCurve,
    updateSimilaritySettings,
    updateGeneralSettings,
    updateApiSettings,
    resetSettings,
    saveSettingsToStorage
  } = useSettings();

  const { providers, hasConfiguredProvider } = useAPIKeys();

  const [saveStatus, setSaveStatus] = useState(null);
  const [showApiSetup, setShowApiSetup] = useState(false);

  const handleSave = async () => {
    const success = await saveSettingsToStorage();
    setSaveStatus(success ? 'Settings saved successfully!' : 'Failed to save settings.');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* API Key Setup Modal */}
      {showApiSetup && (
        <APIKeySetup onClose={() => setShowApiSetup(false)} isModal={true} />
      )}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Configure your grading preferences and application behavior
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
            >
              Save Settings
            </button>
            <button
              onClick={resetSettings}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Reset to Defaults
            </button>
          </div>
        </div>

        {saveStatus && (
          <div className={`mb-6 p-4 rounded-lg ${saveStatus.includes('success') ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'}`}>
            {saveStatus}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Grading Curve Settings */}
          <div className="space-y-6">
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Grading Curve</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-gray-700 dark:text-gray-300">Enable Grading Curve</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.gradingCurve.enabled}
                      onChange={(e) => updateGradingCurve({ enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 dark:peer-focus:ring-primary/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                  </label>
                </div>

                {settings.gradingCurve.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Curve Type
                      </label>
                      <select
                        value={settings.gradingCurve.curveType}
                        onChange={(e) => updateGradingCurve({ curveType: e.target.value })}
                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="linear">Linear</option>
                        <option value="exponential">Exponential</option>
                        <option value="logarithmic">Logarithmic</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                        Curve Strength: <span className="text-primary font-bold">{settings.gradingCurve.curveStrength}%</span>
                      </label>
                      <div className="relative h-6 flex items-center">
                        <div className="absolute left-0 right-0 h-2 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                        <div 
                          className="absolute left-0 h-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 pointer-events-none z-10"
                          style={{ width: `${settings.gradingCurve.curveStrength}%` }}
                        ></div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={settings.gradingCurve.curveStrength}
                          onChange={(e) => updateGradingCurve({ curveStrength: parseInt(e.target.value) })}
                          className="w-full slider-premium relative z-20"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                        Target Average: <span className="text-primary font-bold">{settings.gradingCurve.targetAverage}%</span>
                      </label>
                      <div className="relative h-6 flex items-center">
                        <div className="absolute left-0 right-0 h-2 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                        <div 
                          className="absolute left-0 h-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 pointer-events-none z-10"
                          style={{ width: `${((settings.gradingCurve.targetAverage - 50) / 40) * 100}%` }}
                        ></div>
                        <input
                          type="range"
                          min="50"
                          max="90"
                          value={settings.gradingCurve.targetAverage}
                          onChange={(e) => updateGradingCurve({ targetAverage: parseInt(e.target.value) })}
                          className="w-full slider-premium relative z-20"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                        Minimum Score: <span className="text-primary font-bold">{settings.gradingCurve.minScore}%</span>
                      </label>
                      <div className="relative h-6 flex items-center">
                        <div className="absolute left-0 right-0 h-2 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                        <div 
                          className="absolute left-0 h-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 pointer-events-none z-10"
                          style={{ width: `${(settings.gradingCurve.minScore / 60) * 100}%` }}
                        ></div>
                        <input
                          type="range"
                          min="0"
                          max="60"
                          value={settings.gradingCurve.minScore}
                          onChange={(e) => updateGradingCurve({ minScore: parseInt(e.target.value) })}
                          className="w-full slider-premium relative z-20"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* API Settings */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">API Settings</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Request Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="120"
                    value={settings.apiSettings.timeout}
                    onChange={(e) => updateApiSettings({ timeout: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-gray-700 dark:text-gray-300">Enable Response Caching</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.apiSettings.enableCache}
                      onChange={(e) => updateApiSettings({ enableCache: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 dark:peer-focus:ring-primary/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Retries
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={settings.apiSettings.maxRetries}
                    onChange={(e) => updateApiSettings({ maxRetries: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Retry Delay (ms)
                  </label>
                  <input
                    type="number"
                    min="100"
                    max="5000"
                    step="100"
                    value={settings.apiSettings.retryDelay}
                    onChange={(e) => updateApiSettings({ retryDelay: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Similarity & General Settings */}
          <div className="space-y-6">
            {/* Similarity Settings */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Similarity Analysis</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    Contextual Weight: <span className="text-primary font-bold">{settings.similaritySettings.contextualWeight}%</span>
                  </label>
                  <div className="relative h-6 flex items-center">
                    <div className="absolute left-0 right-0 h-2 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                    <div 
                      className="absolute left-0 h-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 pointer-events-none z-10"
                      style={{ width: `${settings.similaritySettings.contextualWeight}%` }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={settings.similaritySettings.contextualWeight}
                      onChange={(e) => updateSimilaritySettings({ contextualWeight: parseInt(e.target.value) })}
                      className="w-full slider-premium relative z-20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    Semantic Weight: <span className="text-primary font-bold">{settings.similaritySettings.semanticWeight}%</span>
                  </label>
                  <div className="relative h-6 flex items-center">
                    <div className="absolute left-0 right-0 h-2 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                    <div 
                      className="absolute left-0 h-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 pointer-events-none z-10"
                      style={{ width: `${settings.similaritySettings.semanticWeight}%` }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={settings.similaritySettings.semanticWeight}
                      onChange={(e) => updateSimilaritySettings({ semanticWeight: parseInt(e.target.value) })}
                      className="w-full slider-premium relative z-20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    Similarity Threshold: <span className="text-primary font-bold">{settings.similaritySettings.threshold}%</span>
                  </label>
                  <div className="relative h-6 flex items-center">
                    <div className="absolute left-0 right-0 h-2 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                    <div 
                      className="absolute left-0 h-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 pointer-events-none z-10"
                      style={{ width: `${settings.similaritySettings.threshold}%` }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={settings.similaritySettings.threshold}
                      onChange={(e) => updateSimilaritySettings({ threshold: parseInt(e.target.value) })}
                      className="w-full slider-premium relative z-20"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-gray-700 dark:text-gray-300">Strict Mode</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.similaritySettings.strictMode}
                      onChange={(e) => updateSimilaritySettings({ strictMode: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 dark:peer-focus:ring-primary/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-gray-700 dark:text-gray-300">Allow Partial Credit</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.similaritySettings.allowPartialCredit}
                      onChange={(e) => updateSimilaritySettings({ allowPartialCredit: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 dark:peer-focus:ring-primary/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-gray-700 dark:text-gray-300">Enable Context Analysis</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.similaritySettings.enableContext}
                      onChange={(e) => updateSimilaritySettings({ enableContext: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 dark:peer-focus:ring-primary/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* General Settings */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">General Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-gray-700 dark:text-gray-300">Auto-save Settings</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.generalSettings.autoSave}
                      onChange={(e) => updateGeneralSettings({ autoSave: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 dark:peer-focus:ring-primary/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-gray-700 dark:text-gray-300">Show Detailed Feedback</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.generalSettings.showDetailedFeedback}
                      onChange={(e) => updateGeneralSettings({ showDetailedFeedback: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 dark:peer-focus:ring-primary/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Default LLM Provider
                  </label>
                  <select
                    value={settings.generalSettings.defaultLLM}
                    onChange={(e) => updateGeneralSettings({ defaultLLM: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <optgroup label="Local / Free">
                      <option value="ollama">Ollama (Local)</option>
                    </optgroup>
                    <optgroup label="Fast & Budget">
                      <option value="groq">Groq (Ultra-fast)</option>
                      <option value="deepseek">DeepSeek (Low cost)</option>
                      <option value="together">Together AI</option>
                    </optgroup>
                    <optgroup label="Quality">
                      <option value="mistral">Mistral AI</option>
                      <option value="cohere">Cohere</option>
                      <option value="openrouter">OpenRouter (100+ models)</option>
                    </optgroup>
                    <optgroup label="Premium">
                      <option value="openai">OpenAI GPT</option>
                      <option value="anthropic">Anthropic Claude</option>
                      <option value="gemini">Google Gemini</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="custom">Custom API</option>
                    </optgroup>
                  </select>
                </div>

                {/* API Keys Configuration */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      API Keys Configuration
                    </label>
                    <button
                      onClick={() => setShowApiSetup(true)}
                      className="px-3 py-1 text-sm bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
                    >
                      Configure
                    </button>
                  </div>

                  {/* Provider Status */}
                  <div className="space-y-2">
                    {Object.entries(providers).map(([key, provider]) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">{provider.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          provider.enabled && (provider.apiKey || provider.isLocal)
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {provider.enabled && (provider.apiKey || provider.isLocal) ? 'Configured' : 'Not set'}
                        </span>
                      </div>
                    ))}
                  </div>

                  {!hasConfiguredProvider() && (
                    <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                      No AI provider configured. Click "Configure" to set up your API keys or Ollama.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Theme
                  </label>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => updateGeneralSettings({ theme: 'light' })}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                        settings.generalSettings.theme === 'light'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <span>Light</span>
                      </div>
                    </button>
                    <button
                      onClick={() => updateGeneralSettings({ theme: 'dark' })}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                        settings.generalSettings.theme === 'dark'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                        <span>Dark</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
